import moment from "moment-timezone";
import { put, takeEvery, call, select } from "redux-saga/effects";
import { PICTREE } from "../../constants";
import { ENDPOINT } from "../../constants";
import { getJwtToken, getDataFromSxi } from "api";
import {
  setPictreePlotFilterEventOptions,
  setPictreePlotFilterMunicipalityOptions,
  setPictreePlotBreakdownData,
  setPictreePlotBreakdownLoading,
  setPictreePlotEventData,
  setPictreePlotEventLoading,
  setPictreePlotRankingData,
  setPictreePlotRankingLoading,
  setPictreePlotRewardData,
  setPictreePlotRewardLoading,
  setPictreePlotSummaryData,
  setPictreePlotSummaryLoading,
  setPictreePlotTableData,
  setPictreePlotTableLoading,
  setPictreePlotTrendData,
  setPictreePlotTrendLoading,
  setPictreePlotTrustData,
  setPictreePlotTrustLoading,
} from "actions";

export const dateRangePicTree = (state) => state.page.dateRangePicTree;
export const selectedSeason = (state) => state.pictree.selectedSeason;
export const plotMunicipality = (state) => state.page.plotMunicipality;

const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;

const DEFAULT_RANGE_DAYS = 30;
const PLOT_REWARD_COIN_ITEM_ID = "cmryyltn400008nzcge9dcgyx";

const resolveDateRange = (rangeTarget) => {
  if (Array.isArray(rangeTarget) && rangeTarget[0] && rangeTarget[1]) {
    return rangeTarget;
  }

  const end = moment().tz("Asia/Tokyo").format("YYYY-MM-DD");
  const start = moment().tz("Asia/Tokyo").subtract(DEFAULT_RANGE_DAYS, "days").format("YYYY-MM-DD");
  return [start, end];
};

const resolvePlotGameId = (selectedSeasonData) => {
  if (!Array.isArray(selectedSeasonData) || selectedSeasonData.length === 0) {
    return null;
  }

  return selectedSeasonData[0]?.seasonId || null;
};

const buildAreaIdQuery = (municipality) => {
  if (!municipality || municipality === "全体") {
    return null;
  }

  return {
    query: `
      SELECT city.id AS area_id
      FROM game_field_area city
      JOIN game_field_area_display_name city_name
        ON city_name.area_id = city.id
       AND city_name.language_code = 'JA'
      WHERE city_name.name = '${municipality}'
      LIMIT 1;
    `,
    database: "KpiDashboard",
    type: "list",
  };
};

const buildDatePredicate = (alias, rangeTarget, column) => {
  if (!Array.isArray(rangeTarget) || !rangeTarget[0] || !rangeTarget[1]) {
    return "";
  }

  return `
    AND ${alias}.${column} >= '${rangeTarget[0]} 00:00:00'
    AND ${alias}.${column} < DATE_ADD('${rangeTarget[1]} 00:00:00', INTERVAL 1 DAY)
  `;
};

const buildPlotGameIdCondition = (plotGameId, alias = "pp") => (
  plotGameId ? `AND ${alias}.plot_game_id = '${plotGameId}'` : ""
);

const buildPlotScopeWhere = (alias, rangeTarget, plotGameId, areaId, column = "photographed_datetime") => `
  WHERE 1=1
  ${plotGameId ? `AND ${alias}.plot_game_id = '${plotGameId}'` : ""}
  ${areaId ? `AND ${alias}.area_id = '${areaId}'` : ""}
  ${buildDatePredicate(alias, rangeTarget, column)}
`;

const buildSummaryItems = (row) => ([
  { label: "総プロット数", value: Number(row?.total_plot_count || 0), sub: "全アセット合計" },
  { label: "アセット種別数", value: Number(row?.asset_type_count || 0), sub: "井戸 / 電柱 / 消火栓 / 他" },
  { label: "プロットユーザー数", value: Number(row?.plot_user_count || 0), sub: "ユニーク参加者" },
  { label: "1人あたりプロット数", value: Number(row?.plot_per_user_count || 0), sub: "平均" },
  {
    label: "信頼度高 (Lv3+)",
    value: Number(row?.high_confidence_plot_count || 0),
    sub: `全体の ${formatPercent(row?.high_confidence_plot_ratio || 0)}`,
  },
  { label: "宝くじ排出数", value: Number(row?.lottery_issued_count || 0), sub: "報酬コイン換算含む" },
]);

const ASSET_BREAKDOWN_COLOR_RULES = [
  { keywords: ["井戸"], color: "#ef4444" },
  { keywords: ["電柱", "電信柱", "共架柱"], color: "#f97316" },
  { keywords: ["消火栓"], color: "#3b82f6" },
  { keywords: ["マンホール", "ハンドホール"], color: "#8b5cf6" },
  { keywords: ["防火水槽", "給水拠点"], color: "#10b981" },
];

const ASSET_BREAKDOWN_FALLBACK_COLORS = [
  "#ef4444",
  "#f97316",
  "#3b82f6",
  "#8b5cf6",
  "#10b981",
  "#14b8a6",
  "#f59e0b",
  "#ec4899",
];

const buildAssetTableColorRules = ASSET_BREAKDOWN_COLOR_RULES;

const buildTrustLevels = (rowOrRows) => {
  const levels = [
    { score: 20, label: "Lv1（低）", color: "#f87171" },
    { score: 40, label: "Lv2（中）", color: "#fb923c" },
    { score: 60, label: "Lv3（高）", color: "#facc15" },
    { score: 100, label: "Lv4（最高）", color: "#34d399" },
  ];

  const rows = Array.isArray(rowOrRows) ? rowOrRows : rowOrRows ? [rowOrRows] : [];
  const countsByScore = rows.reduce((acc, row) => {
    const score = Number(row?.confidence_score);
    const count = Number(row?.plot_count ?? row?.count ?? row?.total_count ?? 0);
    if (!Number.isNaN(score)) {
      acc[score] = count;
    }
    return acc;
  }, {});
  const totalCount = levels.reduce((sum, level) => sum + Number(countsByScore[level.score] || 0), 0);
  const toPct = (count) => (totalCount > 0 ? Number(((Number(count || 0) / totalCount) * 100).toFixed(1)) : 0);

  return levels.map((level) => ({
    label: level.label,
    count: Number(countsByScore[level.score] || 0),
    pct: toPct(countsByScore[level.score]),
    color: level.color,
  }));
};

const buildLotteryQuery = (rangeTarget, plotGameId, areaId) => ({
  query: `
    SELECT
      COUNT(*) AS lottery_issued_count
    FROM item_grant_event ig
    INNER JOIN item_grant_event_of_plot_photography_reward iger
      ON iger.id = ig.id
    INNER JOIN plot_photography pp
      ON pp.id = iger.plot_photography_id
    WHERE 1=1
    ${plotGameId ? `AND pp.plot_game_id = '${plotGameId}'` : ""}
    ${areaId ? `AND pp.area_id = '${areaId}'` : ""}
    ${buildDatePredicate("ig", rangeTarget, "occurred_datetime")}
  `,
  database: "KpiDashboard",
  type: "list",
});

const buildPlotFilterEventOptionsQuery = (rangeTarget) => ({
  query: `
    WITH plot_games AS (
      SELECT DISTINCT
        pp.plot_game_id
      FROM plot_photography pp
      WHERE 1=1
      ${buildDatePredicate("pp", rangeTarget, "photographed_datetime")}
    )
    SELECT
      pg.plot_game_id AS value,
      COALESCE(pgi.plot_game_name, pg.plot_game_id) AS label
    FROM plot_games pg
    LEFT JOIN plot_game_info pgi
      ON pgi.plot_game_id = pg.plot_game_id
     AND pgi.language_code = 'JA'
    ORDER BY CAST(pg.plot_game_id AS UNSIGNED) ASC;
  `,
  database: "KpiDashboard",
  type: "list",
});

const buildPlotFilterMunicipalityQuery = (rangeTarget, plotGameId) => ({
  query: `
    WITH plot_areas AS (
      SELECT DISTINCT
        pp.area_id
      FROM plot_photography pp
      WHERE 1=1
      ${buildDatePredicate("pp", rangeTarget, "photographed_datetime")}
      ${buildPlotGameIdCondition(plotGameId)}
    )
    SELECT
      pa.area_id AS value,
      COALESCE(adn.name, pa.area_id) AS label
    FROM plot_areas pa
    LEFT JOIN game_field_area_display_name adn
      ON adn.area_id = pa.area_id
     AND adn.language_code = 'JA'
    ORDER BY label ASC;
  `,
  database: "KpiDashboard",
  type: "list",
});

const buildRewardCoinSummaryQuery = (rangeTarget, plotGameId, areaId) => ({
  query: `
    WITH plot_base AS (
      SELECT DISTINCT
        pp.plot_id,
        pp.plot_game_id
      FROM plot_photography pp
      WHERE 1=1
      ${plotGameId ? `AND pp.plot_game_id = '${plotGameId}'` : ""}
      ${areaId ? `AND pp.area_id = '${areaId}'` : ""}
      ${buildDatePredicate("pp", rangeTarget, "photographed_datetime")}
    ),
    reward_def AS (
      SELECT
        pgs.plot_game_id,
        COALESCE(SUM(pgroi.amount), 0) AS coin_amount_per_plot
      FROM plot_game_photography_reward_setting pgs
      INNER JOIN plot_game_photography_reward_bind pgrb
        ON pgrb.plot_game_photography_reward_setting_id = pgs.id
      INNER JOIN plot_game_photography_reward pgr
        ON pgr.id = pgrb.plot_game_photography_reward_id
      INNER JOIN plot_game_photography_reward_of_item pgroi
        ON pgroi.id = pgr.id
      WHERE pgroi.item_id = '${PLOT_REWARD_COIN_ITEM_ID}'
      ${plotGameId ? `AND pgs.plot_game_id = '${plotGameId}'` : ""}
      GROUP BY pgs.plot_game_id
    ),
    lottery_base AS (
      SELECT
        ig.id
      FROM item_grant_event ig
      INNER JOIN item_grant_event_of_plot_photography_reward iger
        ON iger.id = ig.id
      INNER JOIN plot_photography pp
        ON pp.id = iger.plot_photography_id
      WHERE 1=1
      ${plotGameId ? `AND pp.plot_game_id = '${plotGameId}'` : ""}
      ${areaId ? `AND pp.area_id = '${areaId}'` : ""}
      ${buildDatePredicate("ig", rangeTarget, "occurred_datetime")}
    )
    SELECT
      COUNT(*) AS plot_count,
      COALESCE(MAX(rd.coin_amount_per_plot), 0) AS coin_amount_per_plot,
      COALESCE(SUM(COALESCE(rd.coin_amount_per_plot, 0)), 0) AS coin_total_issued,
      (SELECT COUNT(*) FROM lottery_base) AS lottery_issued_count
    FROM plot_base pb
    LEFT JOIN reward_def rd
      ON rd.plot_game_id = pb.plot_game_id;
  `,
  database: "KpiDashboard",
  type: "list",
});

const buildRewardCoinHoldingsQuery = (rangeTarget, plotGameId, areaId) => ({
  query: `
    WITH lottery_base AS (
      SELECT
        ig.player_id,
        ig.occurred_datetime
      FROM item_grant_event ig
      INNER JOIN item_grant_event_of_plot_photography_reward iger
        ON iger.id = ig.id
      INNER JOIN plot_photography pp
        ON pp.id = iger.plot_photography_id
      WHERE 1=1
      ${plotGameId ? `AND pp.plot_game_id = '${plotGameId}'` : ""}
      ${areaId ? `AND pp.area_id = '${areaId}'` : ""}
      ${buildDatePredicate("ig", rangeTarget, "occurred_datetime")}
    )
    SELECT
      lb.player_id AS user,
      COUNT(*) AS count,
      DATE_FORMAT(CONVERT_TZ(MIN(lb.occurred_datetime), 'UTC', 'Asia/Tokyo'), '%Y/%m/%d') AS acquired
    FROM lottery_base lb
    GROUP BY lb.player_id
    ORDER BY count DESC, acquired ASC, user ASC
    LIMIT 4;
  `,
  database: "KpiDashboard",
  type: "list",
});

const toRewardCoinSummaryCards = (row) => {
  const plotCount = Number(row?.plot_count ?? 0);
  const coinAmountPerPlot = Number(row?.coin_amount_per_plot ?? 0);
  const coinTotalIssued = Number(row?.coin_total_issued ?? 0);
  const lotteryIssuedCount = Number(row?.lottery_issued_count ?? 0);

  return [
    {
      label: "コイン総排出数",
      value: coinTotalIssued.toLocaleString("ja-JP"),
      sub: `プロット1件あたり ${coinAmountPerPlot}コイン`,
    },
    {
      label: "宝くじ排出数",
      value: lotteryIssuedCount.toLocaleString("ja-JP"),
      sub: `排出率 ${formatPercent(plotCount > 0 ? (lotteryIssuedCount / plotCount) * 100 : 0)}`,
    },
  ];
};

const toRewardCoinHoldings = (rows) => (Array.isArray(rows) ? rows : []).map((row) => {
  if (Array.isArray(row)) {
    return {
      user: String(pickValue(row?.[0]) || ""),
      count: Number(pickValue(row?.[1]) ?? 0),
      acquired: String(pickValue(row?.[2]) || ""),
    };
  }

  return {
    user: String(row?.user || row?.player_id || row?.name || ""),
    count: Number(row?.count ?? row?.plot_count ?? row?.value ?? 0),
    acquired: String(row?.acquired || row?.acquired_at || row?.acquired_datetime || ""),
  };
}).filter((row) => row.user);

export function* getPictreePlotFilterEventOptions() {
  try {
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = resolveDateRange(yield select(dateRangePicTree));
    const eventRows = yield call(
      getDataFromSxi,
      buildPlotFilterEventOptionsQuery(rangeTarget),
      ENDPOINT.GET_DATA_SXI_DENCHU,
      jwtToken
    );

    const options = (Array.isArray(eventRows) ? eventRows : []).map((row) => ({
      value: String(row?.value || ""),
      label: String(row?.label || row?.value || ""),
    })).filter((row) => row.value);

    yield put(setPictreePlotFilterEventOptions(options));
  } catch (error) {
    console.error("[pictreePlotSaga] failed to load plot event options", error);
    yield put(setPictreePlotFilterEventOptions([]));
  }
}

export function* getPictreePlotFilterMunicipalityOptions(action) {
  try {
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = resolveDateRange(yield select(dateRangePicTree));
    const selectedSeasonData = yield select(selectedSeason);
    const plotGameId = action?.payload?.plotGameId || selectedSeasonData?.[0]?.seasonId || null;
    const municipalityRows = yield call(
      getDataFromSxi,
      buildPlotFilterMunicipalityQuery(rangeTarget, plotGameId),
      ENDPOINT.GET_DATA_SXI_DENCHU,
      jwtToken
    );

    const options = [
      { value: "全体", label: "全体" },
      ...(Array.isArray(municipalityRows) ? municipalityRows : []).map((row) => ({
        value: String(row?.value || ""),
        label: String(row?.label || row?.value || ""),
      })).filter((row) => row.value && row.value !== "全体"),
    ];

    yield put(setPictreePlotFilterMunicipalityOptions(options));
  } catch (error) {
    console.error("[pictreePlotSaga] failed to load plot municipality options", error);
    yield put(setPictreePlotFilterMunicipalityOptions([{ value: "全体", label: "全体" }]));
  }
}

const isValidRange = (range) =>
  Array.isArray(range) &&
  range.length >= 2 &&
  Boolean(range[0]) &&
  Boolean(range[1]);

const resolveEventRange = (rangeTarget) => {
  if (isValidRange(rangeTarget)) {
    return rangeTarget;
  }

  const endDate = moment().tz("Asia/Tokyo").format("YYYY-MM-DD");
  const startDate = moment().tz("Asia/Tokyo").subtract(29, "days").format("YYYY-MM-DD");
  return [startDate, endDate];
};

const pickValue = (cell) =>
  cell?.stringValue ?? cell?.longValue ?? cell?.doubleValue ?? cell?.booleanValue ?? null;

const parseListData = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.body)) {
    return response.body;
  }

  if (typeof response?.body === "string") {
    try {
      return JSON.parse(response.body);
    } catch (error) {
      return [];
    }
  }

  return [];
};

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numeric = Number(value);
  return Number.isNaN(numeric) ? null : numeric;
};

const formatCapturedAt = (value) => {
  if (!value) return null;

  const parsed = moment.tz(value, "Asia/Tokyo");
  if (!parsed.isValid()) {
    return value;
  }

  return `${parsed.format("YYYY-MM-DD HH:mm:ss.SSS")} +00:00:00`;
};

const toPlotTrendPoints = (rows) => (Array.isArray(rows) ? rows : []).map((row) => {
  const label = row?.label || row?.date_label || row?.plot_date || row?.day || "";
  const plotCount = Number(row?.plot_count ?? row?.count ?? row?.total_plot_count ?? 0);
  const userCount = Number(row?.user_count ?? row?.plot_user_count ?? row?.unique_user_count ?? 0);
  return [label, plotCount, userCount];
}).filter(([label]) => Boolean(label));

const resolveAssetBreakdownColor = (label, index) => {
  const matchedRule = ASSET_BREAKDOWN_COLOR_RULES.find((rule) =>
    rule.keywords.some((keyword) => String(label || "").includes(keyword))
  );

  if (matchedRule) {
    return matchedRule.color;
  }

  return ASSET_BREAKDOWN_FALLBACK_COLORS[index % ASSET_BREAKDOWN_FALLBACK_COLORS.length];
};

const toAssetBreakdownItems = (rows) => (Array.isArray(rows) ? rows : []).map((row, index) => {
  const name = String(row?.label || row?.name || row?.asset_name || row?.asset_sub_type || "");
  return {
    name,
    value: Number(row?.plot_count ?? row?.value ?? row?.count ?? 0),
    color: resolveAssetBreakdownColor(name, index),
  };
}).filter((item) => item.name);

const resolveAssetTableColor = (label, index) => {
  const matchedRule = buildAssetTableColorRules.find((rule) =>
    rule.keywords.some((keyword) => String(label || "").includes(keyword))
  );

  if (matchedRule) {
    return matchedRule.color;
  }

  return ASSET_BREAKDOWN_FALLBACK_COLORS[index % ASSET_BREAKDOWN_FALLBACK_COLORS.length];
};

const toPlotAssetTableRows = (rows) => (Array.isArray(rows) ? rows : []).map((row, index) => {
  const name = String(row?.name || row?.label || row?.asset_name || row?.asset_sub_type || "");
  const plotCount = Number(row?.plot_count ?? row?.plotCount ?? 0);
  const lv1 = Number(row?.lv1 ?? row?.lv1_count ?? 0);
  const lv2 = Number(row?.lv2 ?? row?.lv2_count ?? 0);
  const lv3 = Number(row?.lv3 ?? row?.lv3_count ?? 0);
  const lv4 = Number(row?.lv4 ?? row?.lv4_count ?? 0);
  const users = Number(row?.users ?? row?.user_count ?? 0);
  const perUser = Number(row?.per_user ?? row?.perUser ?? (users > 0 ? (plotCount / users).toFixed(2) : 0));

  return {
    name,
    plotCount,
    lv1,
    lv2,
    lv3,
    lv4,
    users,
    perUser: Number.isNaN(perUser) ? 0 : perUser,
    color: resolveAssetTableColor(name, index),
  };
}).filter((item) => item.name);

const toPlotAssetTableSummaryRow = (row) => {
  if (!row) {
    return null;
  }

  const plotCount = Number(row?.plot_count ?? row?.plotCount ?? 0);
  const users = Number(row?.users ?? row?.user_count ?? 0);
  return {
    plotCount,
    lv1: Number(row?.lv1 ?? row?.lv1_count ?? 0),
    lv2: Number(row?.lv2 ?? row?.lv2_count ?? 0),
    lv3: Number(row?.lv3 ?? row?.lv3_count ?? 0),
    lv4: Number(row?.lv4 ?? row?.lv4_count ?? 0),
    users,
    perUser: Number(row?.per_user ?? row?.perUser ?? (users > 0 ? (plotCount / users).toFixed(2) : 0)),
  };
};

const toUserRankingRows = (rows) => (Array.isArray(rows) ? rows : []).map((row) => {
  if (Array.isArray(row)) {
    return {
      name: String(pickValue(row?.[0]) || ""),
      value: Number(pickValue(row?.[1]) ?? 0),
    };
  }

  return {
    name: String(row?.name || row?.label || row?.player_id || row?.user_id || ""),
    value: Number(row?.value ?? row?.plot_count ?? row?.count ?? 0),
  };
}).filter((item) => item.name);

export function* getPictreePlotSummaryData() {
  yield put(setPictreePlotSummaryLoading(true));
  let currentSummaryItems = [];
  try {
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = resolveDateRange(yield select(dateRangePicTree));
    const selectedSeasonData = yield select(selectedSeason);
    const selectedPlotMunicipality = yield select(plotMunicipality);
    currentSummaryItems = yield select((state) => state.pictreePlot.summaryItems);
    const plotGameId = resolvePlotGameId(selectedSeasonData);
    const plotDateFilter = buildDatePredicate("pp", rangeTarget, "photographed_datetime");
    const areaIdQuery = buildAreaIdQuery(selectedPlotMunicipality);
    const areaRows = areaIdQuery
      ? yield call(getDataFromSxi, areaIdQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken)
      : [];
    const areaId = Array.isArray(areaRows) && areaRows[0] ? areaRows[0].area_id : null;

    console.log("[pictreePlotSaga] summary query params", {
      plotGameId,
      areaId,
      rangeTarget,
      selectedSeasonData,
      selectedPlotMunicipality,
    });

    const sqlQuerySummary = {
      query: `
        WITH plot_base AS (
          SELECT DISTINCT
            pp.plot_id,
            pp.player_id
          FROM plot_photography pp
          WHERE 1=1
          ${plotGameId ? `AND pp.plot_game_id = '${plotGameId}'` : ""}
          ${areaId ? `AND pp.area_id = '${areaId}'` : ""}
          ${plotDateFilter}
        )
        SELECT
          COUNT(DISTINCT pb.plot_id) AS total_plot_count,
          COUNT(DISTINCT CONCAT(COALESCE(p.asset_type, ''), '|', COALESCE(p.asset_sub_type, ''))) AS asset_type_count,
          COUNT(DISTINCT pb.player_id) AS plot_user_count,
          ROUND(
            COUNT(DISTINCT pb.plot_id) / NULLIF(COUNT(DISTINCT pb.player_id), 0),
            2
          ) AS plot_per_user_count,
          COUNT(DISTINCT CASE WHEN p.confidence_score >= 60 THEN pb.plot_id END) AS high_confidence_plot_count,
          ROUND(
            COUNT(DISTINCT CASE WHEN p.confidence_score >= 60 THEN pb.plot_id END)
            / NULLIF(COUNT(DISTINCT pb.plot_id), 0) * 100,
            1
          ) AS high_confidence_plot_ratio
        FROM plot_base pb
        JOIN plot p
          ON p.id = pb.plot_id;
      `,
      database: "KpiDashboard",
      type: "list",
    };

    const summaryRows = yield call(getDataFromSxi, sqlQuerySummary, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken);
    console.log("[pictreePlotSaga] summary rows", summaryRows);
    const summaryRow = Array.isArray(summaryRows) ? summaryRows[0] : null;
    if (summaryRow || !Array.isArray(currentSummaryItems) || currentSummaryItems.length === 0) {
      const summaryItems = buildSummaryItems(summaryRow);
      yield put(setPictreePlotSummaryData(summaryItems));
    } else {
      console.warn("[pictreePlotSaga] skip summary overwrite because response was empty");
    }

    try {
      const lotteryRows = yield call(
        getDataFromSxi,
        buildLotteryQuery(rangeTarget, plotGameId, areaId),
        ENDPOINT.GET_DATA_SXI_DENCHU,
        jwtToken
      );
      const lotteryIssuedCount = Number(lotteryRows?.[0]?.lottery_issued_count || 0);
      const baseSummaryItems = summaryRow
        ? buildSummaryItems(summaryRow)
        : (Array.isArray(currentSummaryItems) && currentSummaryItems.length > 0 ? currentSummaryItems : buildSummaryItems(null));
      yield put(setPictreePlotSummaryData(
        baseSummaryItems.map((item) =>
          item.label === "宝くじ排出数"
            ? { ...item, value: lotteryIssuedCount }
            : item
        )
      ));
    } catch (lotteryError) {
      console.error("[pictreePlotSaga] failed to load lottery data", lotteryError);
    }
  } catch (error) {
    console.error("[pictreePlotSaga] failed to load summary data", error);
    if (!Array.isArray(currentSummaryItems) || currentSummaryItems.length === 0) {
      yield put(setPictreePlotSummaryData([]));
    }
  } finally {
    yield put(setPictreePlotSummaryLoading(false));
  }
}

export function* getPictreePlotTrustData() {
  yield put(setPictreePlotTrustLoading(true));
  let currentTrustLevels = [];
  try {
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = resolveDateRange(yield select(dateRangePicTree));
    const selectedSeasonData = yield select(selectedSeason);
    const selectedPlotMunicipality = yield select(plotMunicipality);
    currentTrustLevels = yield select((state) => state.pictreePlot.trustLevels);
    const plotGameId = resolvePlotGameId(selectedSeasonData);
    const plotDateFilter = buildDatePredicate("pp", rangeTarget, "photographed_datetime");
    const areaIdQuery = buildAreaIdQuery(selectedPlotMunicipality);
    const areaRows = areaIdQuery
      ? yield call(getDataFromSxi, areaIdQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken)
      : [];
    const areaId = Array.isArray(areaRows) && areaRows[0] ? areaRows[0].area_id : null;

    console.log("[pictreePlotSaga] trust query params", {
      plotGameId,
      areaId,
      rangeTarget,
      selectedSeasonData,
      selectedPlotMunicipality,
    });

    const sqlQueryTrust = {
      query: `
        WITH plot_base AS (
          SELECT DISTINCT
            pp.plot_id
          FROM plot_photography pp
          WHERE 1=1
          ${plotGameId ? `AND pp.plot_game_id = '${plotGameId}'` : ""}
          ${areaId ? `AND pp.area_id = '${areaId}'` : ""}
          ${plotDateFilter}
        ),
        plot_trust AS (
          SELECT
            COALESCE(p.confidence_score, 0) AS confidence_score,
            COUNT(DISTINCT pb.plot_id) AS plot_count
          FROM plot_base pb
          JOIN plot p
            ON p.id = pb.plot_id
          GROUP BY COALESCE(p.confidence_score, 0)
        )
        SELECT
          confidence_score,
          plot_count
        FROM plot_trust
        ORDER BY confidence_score ASC;
      `,
      database: "KpiDashboard",
      type: "list",
    };

    const trustRows = yield call(getDataFromSxi, sqlQueryTrust, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken);
    console.log("[pictreePlotSaga] trust rows", trustRows);
    if (Array.isArray(trustRows) && trustRows.length > 0) {
      yield put(setPictreePlotTrustData(buildTrustLevels(trustRows)));
    } else if (!Array.isArray(currentTrustLevels) || currentTrustLevels.length === 0) {
      yield put(setPictreePlotTrustData(buildTrustLevels([])));
    } else {
      console.warn("[pictreePlotSaga] skip trust overwrite because response was empty");
    }
  } catch (error) {
    console.error("[pictreePlotSaga] failed to load trust data", error);
    if (!Array.isArray(currentTrustLevels) || currentTrustLevels.length === 0) {
      yield put(setPictreePlotTrustData([]));
    }
  } finally {
    yield put(setPictreePlotTrustLoading(false));
  }
}

export function* getPictreePlotTrendData() {
  yield put(setPictreePlotTrendLoading(true));
  try {
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = resolveDateRange(yield select(dateRangePicTree));
    const selectedSeasonData = yield select(selectedSeason);
    const selectedPlotMunicipality = yield select(plotMunicipality);
    const plotGameId = resolvePlotGameId(selectedSeasonData);
    const areaIdQuery = buildAreaIdQuery(selectedPlotMunicipality);
    const areaRows = areaIdQuery
      ? yield call(getDataFromSxi, areaIdQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken)
      : [];
    const areaId = Array.isArray(areaRows) && areaRows[0] ? areaRows[0].area_id : null;

    console.log("[pictreePlotSaga] trend query params", {
      plotGameId,
      areaId,
      rangeTarget,
      selectedSeasonData,
      selectedPlotMunicipality,
    });

    const trendQuery = {
      query: `
        WITH plot_base AS (
          SELECT DISTINCT
            pp.plot_id,
            pp.player_id,
            DATE(CONVERT_TZ(pp.photographed_datetime, 'UTC', 'Asia/Tokyo')) AS plot_date
          FROM plot_photography pp
          ${buildPlotScopeWhere("pp", rangeTarget, plotGameId, areaId)}
        ),
        plot_trend AS (
          SELECT
            plot_date,
            COUNT(DISTINCT plot_id) AS plot_count,
            COUNT(DISTINCT player_id) AS user_count
          FROM plot_base
          GROUP BY plot_date
        )
        SELECT
          DATE_FORMAT(plot_date, '%c/%e') AS label,
          plot_count,
          user_count
        FROM plot_trend
        ORDER BY plot_date ASC;
      `,
      database: "KpiDashboard",
      type: "list",
    };

    const trendRows = parseListData(
      yield call(getDataFromSxi, trendQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken)
    );
    console.log("[pictreePlotSaga] trend rows", trendRows);
    yield put(setPictreePlotTrendData(toPlotTrendPoints(trendRows)));
  } finally {
    yield put(setPictreePlotTrendLoading(false));
  }
}

export function* getPictreePlotBreakdownData() {
  yield put(setPictreePlotBreakdownLoading(true));
  try {
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = resolveDateRange(yield select(dateRangePicTree));
    const selectedSeasonData = yield select(selectedSeason);
    const selectedPlotMunicipality = yield select(plotMunicipality);
    const plotGameId = resolvePlotGameId(selectedSeasonData);
    const areaIdQuery = buildAreaIdQuery(selectedPlotMunicipality);
    const areaRows = areaIdQuery
      ? yield call(getDataFromSxi, areaIdQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken)
      : [];
    const areaId = Array.isArray(areaRows) && areaRows[0] ? areaRows[0].area_id : null;

    console.log("[pictreePlotSaga] breakdown query params", {
      plotGameId,
      areaId,
      rangeTarget,
      selectedSeasonData,
      selectedPlotMunicipality,
    });

    const breakdownQuery = {
      query: `
        WITH plot_base AS (
          SELECT DISTINCT
            pp.plot_id
          FROM plot_photography pp
          ${buildPlotScopeWhere("pp", rangeTarget, plotGameId, areaId)}
        ),
        asset_breakdown AS (
          SELECT
            COALESCE(name_ja.name, CONCAT(COALESCE(p.asset_type, ''), '|', COALESCE(p.asset_sub_type, ''))) AS label,
            COUNT(DISTINCT pb.plot_id) AS plot_count
          FROM plot_base pb
          JOIN plot p
            ON p.id = pb.plot_id
          LEFT JOIN power_asset_type_name name_ja
            ON name_ja.power_asset_type = p.asset_type
           AND name_ja.power_asset_sub_type = p.asset_sub_type
           AND name_ja.language_code = 'JA'
          GROUP BY
            p.asset_type,
            p.asset_sub_type,
            COALESCE(name_ja.name, CONCAT(COALESCE(p.asset_type, ''), '|', COALESCE(p.asset_sub_type, '')))
          ORDER BY plot_count DESC, label ASC
        )
        SELECT
          label,
          plot_count
        FROM asset_breakdown;
      `,
      database: "KpiDashboard",
      type: "list",
    };

    const breakdownRows = parseListData(
      yield call(getDataFromSxi, breakdownQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken)
    );
    console.log("[pictreePlotSaga] breakdown rows", breakdownRows);
    yield put(setPictreePlotBreakdownData(toAssetBreakdownItems(breakdownRows)));
  } finally {
    yield put(setPictreePlotBreakdownLoading(false));
  }
}

export function* getPictreePlotTableData() {
  yield put(setPictreePlotTableLoading(true));
  try {
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = resolveDateRange(yield select(dateRangePicTree));
    const selectedSeasonData = yield select(selectedSeason);
    const selectedPlotMunicipality = yield select(plotMunicipality);
    const plotGameId = resolvePlotGameId(selectedSeasonData);
    const areaIdQuery = buildAreaIdQuery(selectedPlotMunicipality);
    const areaRows = areaIdQuery
      ? yield call(getDataFromSxi, areaIdQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken)
      : [];
    const areaId = Array.isArray(areaRows) && areaRows[0] ? areaRows[0].area_id : null;

    console.log("[pictreePlotSaga] table query params", {
      plotGameId,
      areaId,
      rangeTarget,
      selectedSeasonData,
      selectedPlotMunicipality,
    });

    const tableQuery = {
      query: `
        WITH plot_base AS (
          SELECT DISTINCT
            pph.plot_id,
            pph.player_id
          FROM plot_photography pph
          WHERE 1=1
          ${plotGameId ? `AND pph.plot_game_id = '${plotGameId}'` : ""}
          ${areaId ? `AND pph.area_id = '${areaId}'` : ""}
          ${buildDatePredicate("pph", rangeTarget, "photographed_datetime")}
        ),
        asset_rows AS (
          SELECT
            COALESCE(name_ja.name, CONCAT(COALESCE(p.asset_type, ''), '|', COALESCE(p.asset_sub_type, ''))) AS name,
            COUNT(DISTINCT pb.plot_id) AS plot_count,
            COUNT(DISTINCT CASE WHEN p.confidence_score = 20 THEN pb.plot_id END) AS lv1,
            COUNT(DISTINCT CASE WHEN p.confidence_score = 40 THEN pb.plot_id END) AS lv2,
            COUNT(DISTINCT CASE WHEN p.confidence_score = 60 THEN pb.plot_id END) AS lv3,
            COUNT(DISTINCT CASE WHEN p.confidence_score = 100 THEN pb.plot_id END) AS lv4,
            COUNT(DISTINCT pb.player_id) AS users
          FROM plot_base pb
          JOIN plot p
            ON p.id = pb.plot_id
          LEFT JOIN power_asset_type_name name_ja
            ON name_ja.power_asset_type = p.asset_type
           AND name_ja.power_asset_sub_type = p.asset_sub_type
           AND name_ja.language_code = 'JA'
          GROUP BY
            p.asset_type,
            p.asset_sub_type,
            COALESCE(name_ja.name, CONCAT(COALESCE(p.asset_type, ''), '|', COALESCE(p.asset_sub_type, '')))
        )
        SELECT
          name,
          plot_count,
          lv1,
          lv2,
          lv3,
          lv4,
          users,
          ROUND(plot_count / NULLIF(users, 0), 2) AS per_user
        FROM asset_rows
        ORDER BY plot_count DESC, name ASC;
      `,
      database: "KpiDashboard",
      type: "list",
    };

    const tableRows = parseListData(
      yield call(getDataFromSxi, tableQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken)
    );
    console.log("[pictreePlotSaga] table rows", tableRows);

    const summaryQuery = {
      query: `
        WITH plot_base AS (
          SELECT DISTINCT
            pph.plot_id,
            pph.player_id
          FROM plot_photography pph
          WHERE 1=1
          ${plotGameId ? `AND pph.plot_game_id = '${plotGameId}'` : ""}
          ${areaId ? `AND pph.area_id = '${areaId}'` : ""}
          ${buildDatePredicate("pph", rangeTarget, "photographed_datetime")}
        )
        SELECT
          COUNT(DISTINCT pb.plot_id) AS plot_count,
          COUNT(DISTINCT CASE WHEN p.confidence_score = 20 THEN pb.plot_id END) AS lv1,
          COUNT(DISTINCT CASE WHEN p.confidence_score = 40 THEN pb.plot_id END) AS lv2,
          COUNT(DISTINCT CASE WHEN p.confidence_score = 60 THEN pb.plot_id END) AS lv3,
          COUNT(DISTINCT CASE WHEN p.confidence_score = 100 THEN pb.plot_id END) AS lv4,
          COUNT(DISTINCT pb.player_id) AS users,
          ROUND(COUNT(DISTINCT pb.plot_id) / NULLIF(COUNT(DISTINCT pb.player_id), 0), 2) AS per_user
        FROM plot_base pb
        JOIN plot p
          ON p.id = pb.plot_id;
      `,
      database: "KpiDashboard",
      type: "list",
    };

    const summaryRows = parseListData(
      yield call(getDataFromSxi, summaryQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken)
    );
    console.log("[pictreePlotSaga] table summary rows", summaryRows);

    const normalizedRows = toPlotAssetTableRows(tableRows);
    yield put(setPictreePlotTableData({
      rows: normalizedRows,
      colors: normalizedRows.map((row) => row.color),
      summaryRow: toPlotAssetTableSummaryRow(summaryRows?.[0]),
    }));
  } finally {
    yield put(setPictreePlotTableLoading(false));
  }
}

export function* getPictreePlotRankingData() {
  yield put(setPictreePlotRankingLoading(true));
  let currentRankingRows = [];
  try {
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = resolveDateRange(yield select(dateRangePicTree));
    const selectedSeasonData = yield select(selectedSeason);
    const selectedPlotMunicipality = yield select(plotMunicipality);
    currentRankingRows = yield select((state) => state.pictreePlot.userRankingRows);
    const plotGameId = resolvePlotGameId(selectedSeasonData);
    const areaIdQuery = buildAreaIdQuery(selectedPlotMunicipality);
    const areaRows = areaIdQuery
      ? yield call(getDataFromSxi, areaIdQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken)
      : [];
    const areaId = Array.isArray(areaRows) && areaRows[0] ? areaRows[0].area_id : null;

    console.log("[pictreePlotSaga] ranking query params", {
      plotGameId,
      areaId,
      rangeTarget,
      selectedSeasonData,
      selectedPlotMunicipality,
    });

    const rankingQuery = {
      query: `
        WITH plot_base AS (
          SELECT DISTINCT
            pph.plot_id,
            pph.player_id
          FROM plot_photography pph
          WHERE 1=1
          ${plotGameId ? `AND pph.plot_game_id = '${plotGameId}'` : ""}
          ${areaId ? `AND pph.area_id = '${areaId}'` : ""}
          ${buildDatePredicate("pph", rangeTarget, "photographed_datetime")}
        )
        SELECT
          pb.player_id AS name,
          COUNT(DISTINCT pb.plot_id) AS value
        FROM plot_base pb
        GROUP BY pb.player_id
        ORDER BY value DESC, name ASC
        LIMIT 10;
      `,
      database: "KpiDashboard",
      type: "list",
    };

    const rankingRows = parseListData(
      yield call(getDataFromSxi, rankingQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken)
    );
    console.log("[pictreePlotSaga] ranking rows", rankingRows);

    const normalizedRows = toUserRankingRows(rankingRows);
    if (normalizedRows.length > 0) {
      yield put(setPictreePlotRankingData(normalizedRows));
    } else if (!Array.isArray(currentRankingRows) || currentRankingRows.length === 0) {
      yield put(setPictreePlotRankingData([]));
    }
  } finally {
    yield put(setPictreePlotRankingLoading(false));
  }
}

export function* getPictreePlotRewardData() {
  yield put(setPictreePlotRewardLoading(true));
  let currentRewardSummaryCards = [];
  let currentRewardHoldings = null;
  try {
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = resolveDateRange(yield select(dateRangePicTree));
    const selectedSeasonData = yield select(selectedSeason);
    const selectedPlotMunicipality = yield select(plotMunicipality);
    currentRewardSummaryCards = yield select((state) => state.pictreePlot.rewardSummaryCards);
    currentRewardHoldings = yield select((state) => state.pictreePlot.rewardHoldings);
    const plotGameId = resolvePlotGameId(selectedSeasonData);
    const areaIdQuery = buildAreaIdQuery(selectedPlotMunicipality);
    const areaRows = areaIdQuery
      ? yield call(getDataFromSxi, areaIdQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken)
      : [];
    const areaId = Array.isArray(areaRows) && areaRows[0] ? areaRows[0].area_id : null;

    console.log("[pictreePlotSaga] reward query params", {
      plotGameId,
      areaId,
      rangeTarget,
      selectedSeasonData,
      selectedPlotMunicipality,
    });

    const rewardSummaryRows = parseListData(
      yield call(
        getDataFromSxi,
        buildRewardCoinSummaryQuery(rangeTarget, plotGameId, areaId),
        ENDPOINT.GET_DATA_SXI_DENCHU,
        jwtToken
      )
    ) || [];
    const rewardSummaryRow = Array.isArray(rewardSummaryRows) ? rewardSummaryRows[0] : null;
    const rewardHoldingsRows = parseListData(
      yield call(
        getDataFromSxi,
        buildRewardCoinHoldingsQuery(rangeTarget, plotGameId, areaId),
        ENDPOINT.GET_DATA_SXI_DENCHU,
        jwtToken
      )
    ) || [];

    const summaryCards = rewardSummaryRow
      ? toRewardCoinSummaryCards(rewardSummaryRow)
      : (Array.isArray(currentRewardSummaryCards) && currentRewardSummaryCards.length > 0
        ? currentRewardSummaryCards
        : toRewardCoinSummaryCards(null));
    const holdings = {
      title: "宝くじ保有状況（上位）",
      rows: rewardHoldingsRows.length > 0
        ? toRewardCoinHoldings(rewardHoldingsRows)
        : (currentRewardHoldings?.rows || []),
    };

    yield put(setPictreePlotRewardData({
      summaryCards,
      holdings,
    }));
  } finally {
    yield put(setPictreePlotRewardLoading(false));
  }
}

export function* getPictreePlotEventData() {
  yield put(setPictreePlotEventLoading(true));
  try {
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = resolveEventRange(yield select((state) => state.page.dateRangePicTree));

    const sqlQuery = {
      query: `
        SELECT
          pph.plot_id AS plot_id,
          pph.player_id AS user_id,
          p.asset_sub_type AS category,
          pph.latitude AS latitude,
          pph.longitude AS longitude,
          DATE_FORMAT(CONVERT_TZ(pph.photographed_datetime, 'UTC', 'Asia/Tokyo'), '%Y-%m-%dT%H:%i:%s+09:00') AS captured_at,
          LPAD(gfa.area_code, 5, '0') AS area_code
        FROM plot_photography pph
        INNER JOIN plot p
          ON p.id = pph.plot_id
        INNER JOIN game_field_area gfa
          ON gfa.id = pph.area_id
        WHERE CONVERT_TZ(pph.photographed_datetime, 'UTC', 'Asia/Tokyo')
          BETWEEN '${rangeTarget[0]} 00:00:00' AND '${rangeTarget[1]} 23:59:59'
        ORDER BY pph.photographed_datetime DESC, pph.id DESC;
      `,
      database: "KpiDashboard",
      type: "list",
    };

    const response = yield call(getDataFromSxi, sqlQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken);
    const rows = parseListData(response);
    const normalizedRows = rows.map((row) => {
      if (Array.isArray(row)) {
        return [
          pickValue(row?.[0]),
          pickValue(row?.[1]),
          pickValue(row?.[2]),
          toNumberOrNull(pickValue(row?.[3])),
          toNumberOrNull(pickValue(row?.[4])),
          formatCapturedAt(pickValue(row?.[5])),
          pickValue(row?.[6]),
        ];
      }

      return [
        row?.plot_id ?? null,
        row?.user_id ?? null,
        row?.category ?? null,
        toNumberOrNull(row?.latitude),
        toNumberOrNull(row?.longitude),
        formatCapturedAt(row?.captured_at),
        row?.area_code ?? null,
      ];
    });

    yield put(setPictreePlotEventData(normalizedRows));
  } catch (err) {
    console.error(err);
    yield put(setPictreePlotEventData([]));
  } finally {
    yield put(setPictreePlotEventLoading(false));
  }
}

export default function* pictreePlotSaga() {
  yield takeEvery(PICTREE.GET_PICTREE_PLOT_FILTER_EVENT_OPTIONS, getPictreePlotFilterEventOptions);
  yield takeEvery(PICTREE.GET_PICTREE_PLOT_FILTER_MUNICIPALITY_OPTIONS, getPictreePlotFilterMunicipalityOptions);
  yield takeEvery(PICTREE.GET_PLOT_SUMMARY_DATA, getPictreePlotSummaryData);
  yield takeEvery(PICTREE.GET_PLOT_TRUST_DATA, getPictreePlotTrustData);
  yield takeEvery(PICTREE.GET_PLOT_TREND_DATA, getPictreePlotTrendData);
  yield takeEvery(PICTREE.GET_PLOT_BREAKDOWN_DATA, getPictreePlotBreakdownData);
  yield takeEvery(PICTREE.GET_PLOT_TABLE_DATA, getPictreePlotTableData);
  yield takeEvery(PICTREE.GET_PLOT_RANKING_DATA, getPictreePlotRankingData);
  yield takeEvery(PICTREE.GET_PLOT_REWARD_DATA, getPictreePlotRewardData);
  yield takeEvery(PICTREE.GET_PLOT_EVENT_DATA, getPictreePlotEventData);
}
